/**
 * ModuleCard
 * Cyberpunk-styled card representing a single integrated module item.
 */

import React from 'react';
import { type ModuleItem } from '../../modules/moduleRegistry';
import { useUIStore } from '@flow-studio/store';

interface ModuleCardProps {
  item: ModuleItem;
  onClick: (item: ModuleItem) => void;
}

export const ModuleCard: React.FC<ModuleCardProps> = ({ item, onClick }) => {
  const { theme } = useUIStore();
  const [hovered, setHovered] = React.useState(false);
  const isDark = theme === 'dark';
  const accent = item.accentColor;
  const isEdgeStream = item.sourceModule === 'edge-stream';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(item)}
      onKeyDown={(e) => e.key === 'Enter' && onClick(item)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '16px',
        borderRadius: '12px',
        border: `1px solid ${hovered ? accent : isDark ? `${accent}30` : '#e5e7eb'}`,
        background: isDark
          ? hovered ? `${accent}10` : 'rgba(255,255,255,0.04)'
          : hovered ? `${accent}08` : 'rgba(255,255,255,0.9)',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        boxShadow: hovered
          ? `0 0 20px ${accent}22, 0 4px 12px rgba(0,0,0,0.2)`
          : isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.08)',
        transform: hovered ? 'translateY(-2px)' : 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        userSelect: 'none',
        outline: 'none',
      }}
    >
      {/* Header row: icon · title · source badge */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <div style={{ color: accent, flexShrink: 0, marginTop: '1px' }}>
          {item.icon}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '14px',
            fontWeight: 600,
            color: isDark ? '#f8fafc' : '#111827',
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {item.label}
          </div>
          <div style={{
            fontSize: '11px',
            color: isDark ? '#64748b' : '#94a3b8',
            marginTop: '2px',
          }}>
            {item.group}
          </div>
        </div>

        <div style={{
          padding: '2px 8px',
          borderRadius: '9999px',
          fontSize: '10px',
          fontWeight: 500,
          letterSpacing: '0.02em',
          background: 'rgba(58,140,69,0.12)',
          color: '#3A8C45',
          border: '1px solid rgba(58,140,69,0.25)',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
          {isEdgeStream ? 'EdgeStream' : 'Explorer'}
        </div>
      </div>

      {/* Description */}
      <div style={{
        fontSize: '12px',
        color: isDark ? '#94a3b8' : '#64748b',
        lineHeight: 1.5,
      }}>
        {item.description}
      </div>

      {/* Open indicator (visible on hover) */}
      {hovered && (
        <div style={{
          fontSize: '11px',
          color: accent,
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
        }}>
          Open module
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="5" y1="12" x2="19" y2="12"/>
            <polyline points="12 5 19 12 12 19"/>
          </svg>
        </div>
      )}
    </div>
  );
};
